import React, { useState, useEffect } from 'react';
import {
  FiBarChart2, FiTrendingUp, FiDollarSign, FiPackage,
  FiCalendar, FiDownload, FiRefreshCw, FiFilter
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useReportes } from '../../hooks/useReportes';

const Reportes = () => {
  const {
    ventasData,
    topProductos,
    isLoading,
    error,
    getReporteVentas,
    getTopProductos,
    setError
  } = useReportes();

  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [topLimit, setTopLimit] = useState(20);

  useEffect(() => {
    // Establecer fechas por defecto (mes actual)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadReports();
    }
  }, [startDate, endDate, period, topLimit]);

  const loadReports = async () => {
    const ventasParams = {
      period,
      start_date: startDate,
      end_date: endDate
    };

    const topParams = {
      limit: topLimit,
      period
    };

    await Promise.all([
      getReporteVentas(ventasParams),
      getTopProductos(topParams)
    ]);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('es-CO', { minimumFractionDigits: 2 })}`;
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return parseFloat(num).toLocaleString('es-CO');
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);

    const today = new Date();
    let firstDay, lastDay;

    switch (newPeriod) {
      case 'week':
        firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
        break;
      case 'month':
        firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'year':
        firstDay = new Date(today.getFullYear(), 0, 1);
        lastDay = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  if (isLoading && !ventasData && !topProductos.length) {
    return <LoadingGrid message="Cargando reportes..." />;
  }

  return (
    <div className="reportes-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <FiBarChart2 style={{ marginRight: '12px' }} />
            Reportes y Análisis
          </h1>
          <p className="page-description">
            Visualiza y analiza el rendimiento de tu negocio
          </p>
        </div>

        <button
          className="btn btn--outline"
          onClick={loadReports}
          disabled={isLoading}
        >
          <FiRefreshCw className={isLoading ? 'spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert--error" style={{ marginBottom: '20px' }}>
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Filtros */}
      <div className="filters-card">
        <div className="filters-header">
          <FiFilter />
          <h3>Filtros</h3>
        </div>

        <div className="filters-content">
          {/* Período predefinido */}
          <div className="filter-group">
            <label className="filter-label">Período</label>
            <div className="period-buttons">
              <button
                className={`period-btn ${period === 'week' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('week')}
              >
                Semana
              </button>
              <button
                className={`period-btn ${period === 'month' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('month')}
              >
                Mes
              </button>
              <button
                className={`period-btn ${period === 'year' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('year')}
              >
                Año
              </button>
            </div>
          </div>

          {/* Rango de fechas personalizado */}
          <div className="filter-group">
            <label className="filter-label">
              <FiCalendar size={16} />
              Desde
            </label>
            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">
              <FiCalendar size={16} />
              Hasta
            </label>
            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          {/* Límite de top productos */}
          <div className="filter-group">
            <label className="filter-label">Top Productos</label>
            <select
              className="form-input"
              value={topLimit}
              onChange={(e) => setTopLimit(parseInt(e.target.value))}
            >
              <option value={10}>Top 10</option>
              <option value={20}>Top 20</option>
              <option value={50}>Top 50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Resumen de Ventas */}
      {ventasData && (
        <div className="reports-section">
          <div className="section-header">
            <h2 className="section-title">
              <FiDollarSign />
              Resumen de Ventas
            </h2>
          </div>

          <div className="stats-grid">
            <div className="stat-card stat-card--primary">
              <div className="stat-icon">
                <FiDollarSign />
              </div>
              <div className="stat-content">
                <div className="stat-label">Ventas Totales</div>
                <div className="stat-value">{formatCurrency(ventasData.total_sales)}</div>
                {ventasData.total_orders && (
                  <div className="stat-meta">{formatNumber(ventasData.total_orders)} órdenes</div>
                )}
              </div>
            </div>

            <div className="stat-card stat-card--success">
              <div className="stat-icon">
                <FiTrendingUp />
              </div>
              <div className="stat-content">
                <div className="stat-label">Promedio por Venta</div>
                <div className="stat-value">{formatCurrency(ventasData.average_sale)}</div>
                {ventasData.growth_rate && (
                  <div className={`stat-meta ${ventasData.growth_rate >= 0 ? 'positive' : 'negative'}`}>
                    {ventasData.growth_rate >= 0 ? '+' : ''}{ventasData.growth_rate}%
                  </div>
                )}
              </div>
            </div>

            {ventasData.total_items && (
              <div className="stat-card stat-card--info">
                <div className="stat-icon">
                  <FiPackage />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Productos Vendidos</div>
                  <div className="stat-value">{formatNumber(ventasData.total_items)}</div>
                  {ventasData.unique_products && (
                    <div className="stat-meta">{ventasData.unique_products} productos únicos</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tabla de desglose por categoría/período */}
          {ventasData.breakdown && ventasData.breakdown.length > 0 && (
            <div className="breakdown-section">
              <h3 className="breakdown-title">Desglose Detallado</h3>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th className="text-right">Ventas</th>
                      <th className="text-right">Órdenes</th>
                      <th className="text-right">Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasData.breakdown.map((item, index) => (
                      <tr key={index}>
                        <td>{item.category || item.date || 'N/A'}</td>
                        <td className="text-right">{formatCurrency(item.total_sales)}</td>
                        <td className="text-right">{formatNumber(item.total_orders)}</td>
                        <td className="text-right">{formatCurrency(item.average_sale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Productos */}
      {topProductos && topProductos.length > 0 && (
        <div className="reports-section">
          <div className="section-header">
            <h2 className="section-title">
              <FiPackage />
              Productos Más Vendidos
            </h2>
            <button className="btn btn--outline btn--sm">
              <FiDownload />
              Exportar
            </button>
          </div>

          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>#</th>
                  <th>Producto</th>
                  <th className="text-right">Cantidad</th>
                  <th className="text-right">Ventas</th>
                  <th className="text-right">% Total</th>
                </tr>
              </thead>
              <tbody>
                {topProductos.map((producto, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`rank-badge rank-${index < 3 ? index + 1 : 'default'}`}>
                        {index + 1}
                      </span>
                    </td>
                    <td>
                      <div className="product-cell">
                        <div className="product-name">{producto.product_name || producto.name}</div>
                        {producto.code && (
                          <div className="product-code">Código: {producto.code}</div>
                        )}
                      </div>
                    </td>
                    <td className="text-right">
                      <span className="quantity-badge">{formatNumber(producto.quantity_sold)}</span>
                    </td>
                    <td className="text-right">
                      <strong>{formatCurrency(producto.total_revenue)}</strong>
                    </td>
                    <td className="text-right">
                      {producto.percentage && (
                        <span className="percentage">{producto.percentage}%</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Estado vacío */}
      {!isLoading && !ventasData && !topProductos.length && (
        <div className="empty-state">
          <FiBarChart2 size={64} />
          <h3>No hay datos para mostrar</h3>
          <p>Ajusta los filtros para ver los reportes</p>
        </div>
      )}
    </div>
  );
};

export default Reportes;
